import { BridgeFiatRail } from 'services/merchants/types';

import { RequestPagination, ServerPagination } from 'types/pagination';
import { RequestSorting } from 'types/sort';

/** List scope is `accountUuid` in the URL; extend when the API accepts body filters. */
export type PayoutsListFilters = Record<string, never>;

export interface PayoutsListQuery {
  pagination?: RequestPagination;
  sort?: RequestSorting;
  filters?: PayoutsListFilters | undefined;
}

/** POST body for list; `accountUuid` is taken from the URL path, not sent in the body. */
export type PayoutsListRequest = PayoutsListQuery & { accountUuid: string };

export interface PayoutQuery {
  sort?: RequestSorting;
  accountUuid: string;
  /** Settlement / payout record id in the URL path. */
  payoutUuid: string;
}

export enum PayoutDestinationType {
  BANK_ACCOUNT = 'bank_account',
  CRYPTO = 'crypto',
}

export interface PayoutDestinationDetails {
  type: PayoutDestinationType;
  assets: string[];
  fee: {
    fixed: number;
    percent: number;
  };
  minAmount: number;
  network: BridgeFiatRail | string;
}

export enum BridgeTransferState {
  AWAITING_FUNDS = 'awaiting_funds',
  IN_REVIEW = 'in_review',
  FUNDS_RECEIVED = 'funds_received',
  PAYMENT_SUBMITTED = 'payment_submitted',
  PAYMENT_PROCESSED = 'payment_processed',
  CANCELED = 'canceled',
  COMPLETED = 'COMPLETED',
  INITIATED = 'INITIATED',
  SUBMITTED = 'SUBMITTED',
  ERROR = 'error',
  RETURNED = 'returned',
  REFUNDED = 'refunded',
  FAILED = 'FAILED',
}

export type Payout = {
  uuid: string;
  merchantUuid: string;
  periodStart: string;
  periodEnd: string;
  status: BridgeTransferState;
  destinationType: PayoutDestinationType;
  destinationUuid: string;
  destinationTracking: string;
  currency: string;
  amount: number;
  /** Prefer for display: merchant-facing payout net amount. */
  payoutNetAmount?: number;
  submittedAt: string;
};

export enum PayoutTotalType {
  ORDERS = 'Orders',
  FEES = 'Fees',
}

export enum PayoutTransactionType {
  CHARGE = 'Charge',
  TRANSFER_FEE = 'Transfer Fee',
}

export interface PayoutDestination {
  status: BridgeTransferState;
  paymentRail: string;
  bankAccount?: {
    uuid: string;
    name: string;
    traceId: string;
    memo?: string | null;
  };
  crypto?: {
    wallet: string;
    onChain: {
      transactionHash: string;
      explorerUrl: string;
    };
  };
}

export interface PayoutPublicModel {
  uuid: string;
  merchantUuid: string;

  createdAt: string;
  submittedAt?: string | null;
  amount: number;
  /** Prefer for display: merchant-facing payout net amount. Falls back to amount on older APIs. */
  payoutNetAmount?: number;
  currency: string;
  payoutDate: string;
  periodStart: string;
  periodEnd: string;
  totals: {
    type: PayoutTotalType;
    count: number;
    amount: number;
  }[];
  transactions: {
    type: PayoutTransactionType;
    source: string;
    amount: number;
    fee: number;
  }[];
  destination: PayoutDestination;
  status: BridgeTransferState;
  statusReason: string | null;
  isRetryable: boolean;
}

export type PayoutsListResponse = { data: Payout[]; pagination: ServerPagination };
export type PayoutInfoResponse = { data: PayoutPublicModel };
